import React, {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  TypedUseSelectorHook,
  useDispatch,
  useSelector as useReduxSelector,
} from 'react-redux';

import debounce from 'lodash.debounce';

import {
  Autocomplete,
  Box,
  Paper,
  SelectChangeEvent,
  TextField,
} from '@mui/material';

import { RootState } from '../../app/rootReducer';
import { shouldShowMasterView } from '../../common/actionHelper';
import { apiEndPoints } from '../../common/api.endPoints';
import FilterDateRange from '../../common/components/FilterDateRange';
import SearchInput from '../../common/components/SearchInput';
import { DecisionFilterType } from '../../common/constants/decision';
import { FeatureFlags } from '../../common/constants/featureFlags';
import { PIALifecycles, RUWLifecycles } from '../../common/constants/lifecycle';
import {
  getCarrierRoles,
  isNonCarrierRolePresent,
  RoleDetailedLifeCycleMap,
} from '../../common/constants/roles';
import { SharedConstants } from '../../common/constants/shared';
import { UserOrganization } from '../../common/constants/userOrganization';
import { FeatureFlagsContext } from '../../common/featureFlagsWrapper';
import { HttpService } from '../../common/http.service';
import { IFilter } from '../../common/interfaces/IFilter';
import { IKeyValMap } from '../../common/interfaces/IKeyValMap';
import MetadataService from '../../common/metadata.service';
import { ProductFactory } from '../../common/productConfig/productFactory';
import { AuthContext } from '../login/AuthWrapper';
import { getTaskTypeFilter } from '../tasks/types/forReviewFilter';
import { FilterSelect } from './FilterSelect';
import { filterActions } from './slices/statusSlice';
import styles from './styles.module.scss';

export const useSelector: TypedUseSelectorHook<RootState> = useReduxSelector;

function updateFilter(all: IFilter[], selected: IFilter[]): IFilter[] {
  return all.filter(item => selected.some(s => s.value === item.value));
}

export const FilterBar = () => {
  const [decisionFilter, setDecisionFilter] = useState<IFilter[]>([]);
  const [stageFilter, setStageFilter] = useState<IFilter[]>([]);
  const [lifecycleFilters, setLifecycleFilters] = useState<IFilter[]>([]);
  const [detailedLifecycleFilters, setDetailedLifecycleFilters] = useState<
    IFilter[]
  >([]);
  const [forReviewFilter, setForReviewFilter] = useState<IFilter[]>([]);
  const [userFilter, setUserFilter] = useState<IFilter[]>([]);
  const [carrierUserFilter, setCarrierUserFilter] = useState<IFilter[]>([]);
  const [productFilter, setProductFilter] = useState<IFilter[]>([]);
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [agencySearchFilter, setAgencySearchFilter] = useState<string>('');

  const currDecisionFilters: IFilter[] = useSelector<IFilter[]>(
    state => state.filters.decisionFilters,
  );
  const currStageFilters: IFilter[] = useSelector(
    state => state.filters.stageFilters,
  );
  const currLifecycleFilters: IFilter[] = useSelector(
    state => state.filters.lifecycleFilters,
  );

  const currDetailedLifecycleFilters: IFilter[] = useSelector(
    state => state.filters.detailedLifecycleFilters,
  );
  const currUserFilters: IFilter[] = useSelector(
    state => state.filters.userFilters,
  );
  const currCarrierUserFilters: IFilter[] = useSelector(
    state => state.filters.carrierUserFilter,
  );
  const currProductFilters: IFilter[] = useSelector(
    state => state.filters.productFilters,
  );
  const forReviewFilters: IFilter[] = useSelector(
    state => state.filters.forReviewFilter,
  );

  const filtersInitialized: any = useSelector(
    state => state.filters.initialized,
  );

  // Current date filters from the store
  const oldSearchFilters: string = useSelector(
    state => state.filters.searchFilter,
  );
  const oldAgencySearchFilters: string = useSelector(
    state => state.filters.agencySearchFilter,
  );
  const fromDate: string = useSelector(state => state.filters.fromDate);
  const toDate: string = useSelector(state => state.filters.toDate);

  const dispatch = useDispatch();
  const auth = useContext(AuthContext);
  const { flags } = useContext(FeatureFlagsContext);
  const isAgencySearchEnabled = flags[FeatureFlags.UW_NORA_AGENCY_SEARCH_ENABLED] || false;
  const isNonCarrierRole = isNonCarrierRolePresent(auth?.roles);
  const carrierRoles = getCarrierRoles(auth?.roles);

  const debouncedSearch = useCallback(
    debounce((searchText: string) => {
      dispatch(filterActions.onSearchFilterChange({ search: searchText }));
    }, SharedConstants.DEBOUNCE_TIME),
    [dispatch],
  );

  const debouncedAgencySearch = useCallback(
    debounce((searchText: string) => {
      dispatch(
        filterActions.onAgencySearchFilterChange({ agencySearch: searchText }),
      );
    }, SharedConstants.DEBOUNCE_TIME),
    [dispatch],
  );

  useEffect(() => {
    dispatch(filterActions.onPrioritizedViewChange());
  }, [auth.prioritizedView]);

  useEffect(() => {
    (async () => {
      const options: IFilter[] = getTaskTypeFilter(auth.userOrganization);
      dispatch(
        filterActions.onForReviewFilterChange({
          forReviewFilter: updateFilter(options, forReviewFilters),
        }),
      );
      setForReviewFilter(options);
    })();

    (async () => {
      const data: IKeyValMap = await HttpService.getInstance().get(
        `${apiEndPoints.decisionTypes}?type=${DecisionFilterType.FINAL}`,
      );
      const items = data.parsedBody;
      if (items?.length) {
        const filters = items.map((item: any) => ({
          value: item.name,
          label: item.name,
        }));
        dispatch(
          filterActions.onDecisionFilterChange({
            decisionFilter: updateFilter(filters, currDecisionFilters),
          }),
        );
        setDecisionFilter(filters);
      }
    })();

    (async () => {
      const stages: IKeyValMap = await MetadataService.getApplicationStages();
      const options: IFilter[] = Object.keys(stages).map(stageName => ({
        value: stageName,
        label: stages[stageName]?.displayText || '',
      }));
      dispatch(
        filterActions.onStageFilterChange({
          stageFilter: updateFilter(options, currStageFilters),
        }),
      );
      setStageFilter(options);
    })();

    (async () => {
      const lifecycles: IKeyValMap =
        await MetadataService.getApplicationLifecycles();
      const options = Object.keys(lifecycles).map(lifecycleName => ({
        value: lifecycleName,
        label: lifecycles[lifecycleName]?.displayText || lifecycleName,
      }));

      dispatch(
        filterActions.onLifecycleFilterChange({
          lifecycleFilters: updateFilter(options, currLifecycleFilters),
        }),
      );
      setLifecycleFilters(options);
    })();

    (async () => {
      const detailedLifecycle: IKeyValMap =
        await MetadataService.getApplicationDetailedLifecycles();
      const NOT_SUPPORTED_DETAILED_LIFECYCLES = [
        'REINSTATEMENT_PEER_REVIEW',
        'PLAN_F_AUDIT_PEER_REVIEW',
      ];

      const options = Object.keys(detailedLifecycle)
        .map(detailedLifecycleName => ({
          value: detailedLifecycleName,
          label:
            detailedLifecycle[detailedLifecycleName]?.displayText ||
            detailedLifecycleName,
        }))
        .filter(
          option => !NOT_SUPPORTED_DETAILED_LIFECYCLES.includes(option.value),
        );

      dispatch(
        filterActions.onDetailedLifecycleFilterChange({
          detailedLifecycleFilters: updateFilter(options, currLifecycleFilters),
        }),
      );
      setDetailedLifecycleFilters(options);
    })();

    (async () => {
      const data: IKeyValMap = await HttpService.getInstance().get(
        apiEndPoints.carrierunderwriters,
      );
      const items = data.parsedBody;
      if (items && items.length && items[0].id) {
        items.push({ id: -1, displayName: '-' });
        const users: IFilter[] = items.map((item: any) => ({
          value: item.id?.toString(),
          label: item.displayName,
        }));
        dispatch(
          filterActions.onCarrierUserFilterChange({
            carrierUserFilter: updateFilter(users, currCarrierUserFilters),
          }),
        );
        setCarrierUserFilter(users);
      }
    })();

    (async () => {
      const data: IKeyValMap = await HttpService.getInstance().get(
        apiEndPoints.users,
      );
      const items = data.parsedBody;
      if (items && items.length && items[0].id) {
        items.push({ id: -1, displayName: '-' });
        const users: IFilter[] = items.map((item: any) => ({
          value: item.id?.toString(),
          label: item.displayName,
        }));
        dispatch(
          filterActions.onUserFilterChange({
            userFilter: updateFilter(users, currUserFilters),
          }),
        );
        setUserFilter(users);
      }
    })();

    const productFilters = userOrganizationToProductFilters(
      auth.userOrganization as UserOrganization,
    );
    dispatch(
      filterActions.onProductFilterChange({
        productFilter: updateFilter(productFilters, currProductFilters),
      }),
    );
    setProductFilter(productFilters);

    setSearchFilter(oldSearchFilters);
    setAgencySearchFilter(oldAgencySearchFilters);
  }, []);

  const userOrganizationToProductFilters = (
    userOrganization: UserOrganization,
  ): IFilter[] => {
    const result: IFilter[] = [];
    const products = ProductFactory.getProductsByOrganization(userOrganization);
    products.forEach(product => {
      const subCat = product.getSubCategory();
      result.push({
        value: subCat
          ? `${product.getProduct()}:${subCat}`
          : product.getProduct(),
        label: product.getProductDisplayText(),
      });
    });
    return result;
  };

  useEffect(() => {
    if (
      !auth.loggedIn ||
      !userFilter?.length ||
      !stageFilter?.length ||
      !lifecycleFilters?.length ||
      !detailedLifecycleFilters?.length ||
      filtersInitialized
    ) {
      return;
    }
    (async () => {
      const showMasterView = shouldShowMasterView(auth.roles);
      // const stages: IKeyValMap = await MetadataService.getApplicationStages();
      const lifecycles: IKeyValMap =
        await MetadataService.getApplicationLifecycles();
      const selectedLifecycleFilters = Array<string>();
      const selectedDetailedLifecycleFilters = Array<string>();

      if (carrierRoles) {
        const hasValidMappings = carrierRoles.every(carrierRole =>
          RoleDetailedLifeCycleMap.has(carrierRole),
        );

        if (hasValidMappings) {
          selectedDetailedLifecycleFilters.push(
            ...carrierRoles.map(
              carrierRole => RoleDetailedLifeCycleMap.get(carrierRole)!,
            ),
          );
        }
      }
      dispatch(
        filterActions.onInitializationChange({
          stageFilter: [],
          lifecycleFilters: updateFilter(
            lifecycleFilters,
            selectedLifecycleFilters.map(lifecycleName => ({
              value: lifecycleName,
              label: lifecycles[lifecycleName]?.displayText || '',
            })),
          ),
          detailedLifecycleFilters: updateFilter(
            detailedLifecycleFilters,
            selectedDetailedLifecycleFilters.map(detailedLifecycleName => ({
              value: detailedLifecycleName,
              label: lifecycles[detailedLifecycleName]?.displayText || '',
            })),
          ),
          carrierUserFilter: updateFilter(carrierUserFilter, []),
          fromDate: auth.prioritizedView ? fromDate : undefined,
          toDate: auth.prioritizedView ? toDate : undefined,
          userFilter: updateFilter(
            userFilter,
            showMasterView
              ? []
              : ([
                  { value: auth?.id?.toString(), label: auth.userName },
                ] as IFilter[]),
          ),
        }),
      );
    })();
  }, [
    auth.loggedIn,
    userFilter,
    stageFilter,
    lifecycleFilters,
    detailedLifecycleFilters,
  ]);

  const handleDecisionChange = (event: SelectChangeEvent<IFilter[]>) => {
    const {
      target: { value },
    } = event;
    dispatch(
      filterActions.onDecisionFilterChange({
        decisionFilter: value as IFilter[],
      }),
    );
  };

  const handleStageChange = (event: SelectChangeEvent<IFilter[]>) => {
    const value = event?.target?.value;
    if (!value) return;
    dispatch(
      filterActions.onStageFilterChange({
        stageFilter: value as IFilter[],
      }),
    );
  };

  // Lifecycle filters only apply to carrier underwriter roles
  const handleLifecycleChange = (event: SelectChangeEvent<IFilter[]>) => {
    const value = event?.target?.value;
    if (!value) return;
    dispatch(
      filterActions.onLifecycleFilterChange({
        lifecycleFilters: value as IFilter[],
      }),
    );
  };

  const handleDetailedLifecycleChange = (
    event: SelectChangeEvent<IFilter[]>,
  ) => {
    const value = event?.target?.value;
    if (!value) return;
    dispatch(
      filterActions.onDetailedLifecycleFilterChange({
        detailedLifecycleFilters: value as IFilter[],
      }),
    );
  };

  const handleForReviewChange = (event: SelectChangeEvent<IFilter[]>) => {
    const value = event?.target?.value as IFilter[];
    if (!value) return;
    dispatch(
      filterActions.onForReviewFilterChange({
        forReviewFilter: value,
      }),
    );
  };

  const handleProductChange = (event: SelectChangeEvent<IFilter[]>) => {
    const value = event?.target?.value;
    if (!value) return;
    dispatch(
      filterActions.onProductFilterChange({
        productFilter: value as IFilter[],
      }),
    );
  };

  const handleCarrierUserChange = (
    _event: React.SyntheticEvent,
    value: IFilter[],
    _reason: string,
  ) => {
    dispatch(
      filterActions.onCarrierUserFilterChange({ carrierUserFilter: value }),
    );
  };

  const handleAssigneeChange = (
    _event: React.SyntheticEvent,
    value: IFilter[],
    _reason: string,
  ) => {
    dispatch(filterActions.onUserFilterChange({ userFilter: value }));
  };

  const onSearch = (event: ChangeEvent<HTMLInputElement>) => {
    const value: string = event?.target?.value ?? '';
    debouncedSearch(value);
    setSearchFilter(value);
  };

  const onAgencySearch = (event: ChangeEvent<HTMLInputElement>) => {
    const value: string = event?.target?.value ?? '';
    debouncedAgencySearch(value);
    setAgencySearchFilter(value);
  };

  // Handle date range changes
  const handleFilterDateSelect = (minDate: string, maxDate: string) => {
    // Dispatch actions to set fromDate and toDate in the store
    dispatch(filterActions.onFromDateChange({ fromDate: minDate }));
    dispatch(filterActions.onToDateChange({ toDate: maxDate }));
  };

  const handleClearDate = () => {
    // Clear the date filters in the store
    dispatch(filterActions.onFromDateChange({ fromDate: '' }));
    dispatch(filterActions.onToDateChange({ toDate: '' }));
  };

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
      debouncedAgencySearch.cancel();
    };
  }, [debouncedSearch, debouncedAgencySearch]);

  return (
    <Box className={styles.Toolbar} display="flex" flexWrap="wrap" width="100%">
      <SearchInput onChange={onSearch} searchFilter={searchFilter} />
      {isAgencySearchEnabled && (
        <SearchInput
          onChange={onAgencySearch}
          searchFilter={agencySearchFilter}
          placeholder="Search by partner"
        />
      )}
      <FilterSelect
        filterName="Product"
        options={productFilter}
        selected={currProductFilters}
        onChange={handleProductChange}
      />
      <FilterSelect
        filterName="Final Decision"
        options={decisionFilter}
        selected={currDecisionFilters}
        onChange={handleDecisionChange}
      />

      {/*
        Underwriter
        V1: Shows stage filter only
        V2: Shows stage filter and detailed lifecycle filter
      */}
      {/*
        Carrier Underwriter:
          V1: Shows lifecycle filter only
          V2: Shows stage and detailed lifecycle filter
      */}

      {/* Detailed lifecycle filter */}
      <FilterSelect
        filterName="Detailed Lifecycle"
        options={detailedLifecycleFilters}
        selected={currDetailedLifecycleFilters}
        onChange={handleDetailedLifecycleChange}
      />

      {/* Stage filter */}
      <FilterSelect
        filterName="Stage"
        options={stageFilter}
        selected={currStageFilters}
        onChange={handleStageChange}
      />

      <FilterSelect
        filterName="For review"
        options={forReviewFilter}
        selected={forReviewFilters}
        onChange={handleForReviewChange}
      />
      <FilterDateRange
        title="Date range"
        defaultValue={{ fromDate, toDate }}
        onFilterDateSelect={handleFilterDateSelect}
        onClearDate={handleClearDate}
        allowClear
      />
      {isNonCarrierRole && (
        <Autocomplete
          multiple
          disablePortal
          autoHighlight
          limitTags={1}
          size={'small'}
          options={userFilter.sort((a, b) => a.label.localeCompare(b.label))}
          value={currUserFilters}
          onChange={handleAssigneeChange}
          sx={{
            fontSize: '14.75px',
            minWidth: '200px',
            '.MuiChip-root.MuiChip-sizeSmall': {
              height: '20px',
            },
            '.MuiOutlinedInput-input.MuiAutocomplete-input': {
              '&::placeholder': {
                color: '#7E8590!important',
                opacity: 100,
              },
            },
            '.MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
              padding: '2px 2px 2px 6px',
              fontSize: '14.75px',
              minHeight: '32px',
            },
            '.MuiAutocomplete-popupIndicator': {
              padding: '2px',
            },
            '.MuiOutlinedInput-root.MuiOutlinedInput-notchedOutline': {
              borderColor: '#D9DBDF !important',
            },
          }}
          renderInput={selectedFilters => (
            <TextField {...selectedFilters} placeholder="Assignee" />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.value}>
              {option.label}
            </li>
          )}
          slots={{ paper: ({ children }) => <Paper>{children}</Paper> }}
        />
      )}
      {carrierRoles && (
        <Autocomplete
          multiple
          disablePortal
          autoHighlight
          limitTags={1}
          size={'small'}
          options={carrierUserFilter.sort((a, b) =>
            a.label.localeCompare(b.label),
          )}
          value={currCarrierUserFilters}
          onChange={handleCarrierUserChange}
          sx={{
            fontSize: '14.75px',
            minWidth: '200px',
            '.MuiChip-root.MuiChip-sizeSmall': {
              height: '20px',
            },
            '.MuiOutlinedInput-input.MuiAutocomplete-input': {
              '&::placeholder': {
                color: '#7E8590!important',
                opacity: 100,
              },
            },
            '.MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
              padding: '2px 2px 2px 6px',
              fontSize: '14.75px',
              minHeight: '32px',
            },
            '.MuiAutocomplete-popupIndicator': {
              padding: '2px',
            },
            '.MuiOutlinedInput-root.MuiOutlinedInput-notchedOutline': {
              borderColor: '#D9DBDF !important',
            },
          }}
          renderInput={selectedFilters => (
            <TextField {...selectedFilters} placeholder="Carrier Assignee" />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.value}>
              {option.label}
            </li>
          )}
          slots={{ paper: ({ children }) => <Paper>{children}</Paper> }}
        />
      )}
    </Box>
  );
};
